/**
 * 30s preview audio player for locked destinations.
 *
 * Renders an emotional, destination-specific narrated intro (built and cached
 * server-side per language). When the audio ends the player swaps to a lock
 * card with two CTAs: "🎧 Escuchar guía completa" and "🌺 Maya Pass México".
 *
 * Tracks: audio_preview_started / audio_preview_completed / paywall_shown.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Loader2, Lock, Pause, Play, Sparkles } from "lucide-react";
import { MAYA_IMAGE_URL as mayaIcon } from "@/lib/maya-art";
import { cn } from "@/lib/utils";
import { getMayaPreview, startMayaPreviewPlay } from "@/lib/maya-samples.functions";
import { logMayaEvent } from "@/lib/maya-tracking.functions";
import { getMayaAnonSessionId } from "@/lib/maya-session";

type Lang = "es" | "en" | "fr" | "pt";

interface Props {
  destinationKey: string;
  destinationName: string;
  location: string | null;
  summary: string | null;
  lang: Lang;
  /** Open the full paywall when the listener wants more. */
  onUnlockClick?: (kind: "destination" | "pass") => void;
}

const LABELS: Record<Lang, {
  badge: string;
  title: string;
  hint: string;
  play: string;
  pause: string;
  preparing: string;
  ended: string;
  unlock: string;
  pass: string;
  error: string;
  reach: string;
}> = {
  es: {
    badge: "Muestra gratuita · 15s",
    title: "Escucha el inicio narrado por Maya",
    hint: "Una probada del audio guía premium para este destino.",
    play: "Escuchar muestra",
    pause: "Pausar",
    preparing: "Preparando muestra…",
    ended: "🔒 Continúa escuchando con Maya Premium",
    unlock: "🎧 Escuchar guía completa",
    pass: "🌺 Maya Pass México",
    error: "No pudimos preparar la muestra ahora. Inténtalo de nuevo.",
    reach: "Has llegado al final de la muestra.",
  },
  en: {
    badge: "Free sample · 15s",
    title: "Listen to Maya's narrated intro",
    hint: "A taste of the premium audio guide for this destination.",
    play: "Play sample",
    pause: "Pause",
    preparing: "Preparing sample…",
    ended: "🔒 Keep listening with Maya Premium",
    unlock: "🎧 Listen to the full guide",
    pass: "🌺 Maya Pass México",
    error: "We couldn't prepare the sample. Please try again.",
    reach: "You've reached the end of the preview.",
  },
  fr: {
    badge: "Aperçu gratuit · 15s",
    title: "Écoute l'intro narrée par Maya",
    hint: "Un aperçu de l'audio-guide premium pour cette destination.",
    play: "Écouter l'aperçu",
    pause: "Pause",
    preparing: "Préparation de l'aperçu…",
    ended: "🔒 Continue avec Maya Premium",
    unlock: "🎧 Écouter le guide complet",
    pass: "🌺 Maya Pass México",
    error: "Impossible de préparer l'aperçu. Réessaie.",
    reach: "Tu as atteint la fin de l'aperçu.",
  },
  pt: {
    badge: "Amostra grátis · 15s",
    title: "Ouça a introdução narrada por Maya",
    hint: "Uma amostra do áudio guia premium deste destino.",
    play: "Ouvir amostra",
    pause: "Pausar",
    preparing: "Preparando amostra…",
    ended: "🔒 Continue ouvindo com Maya Premium",
    unlock: "🎧 Ouvir o guia completo",
    pass: "🌺 Maya Pass México",
    error: "Não foi possível preparar a amostra. Tente novamente.",
    reach: "Você chegou ao fim da prévia.",
  },
};

const PREVIEW_MAX_SECONDS = 15;

export function MayaAudioPreview({
  destinationKey,
  destinationName,
  location,
  summary,
  lang,
  onUnlockClick,
}: Props) {
  const labels = LABELS[lang];
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [ended, setEnded] = useState(false);
  const [progress, setProgress] = useState(0);
  const [starting, setStarting] = useState(false);
  const [playError, setPlayError] = useState<string | null>(null);
  const [limitReached, setLimitReached] = useState(false);
  const anonId = useMemo(() => getMayaAnonSessionId(), []);
  const deviceId = anonId || undefined;

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["maya-preview", destinationKey, lang, deviceId],
    queryFn: () =>
      getMayaPreview({
        data: {
          destinationKey,
          destinationName,
          location: location ?? null,
          summary: summary ?? null,
          lang,
          deviceId,
        },
      }),
    enabled: !!destinationKey,
    staleTime: 1000 * 60 * 5,
    retry: 1,
  });

  // Sync server-authoritative limit into local UI state.
  useEffect(() => {
    if (data?.limitReached) {
      setLimitReached(true);
      setEnded(true);
    }
  }, [data?.limitReached]);

  // Fire paywall_shown when the lock card appears.
  useEffect(() => {
    if (ended) {
      void logMayaEvent({
        data: {
          event_type: "paywall_shown",
          anon_session_id: anonId,
          destination: destinationKey,
          lang,
        },
      });
    }
  }, [ended, anonId, destinationKey, lang]);

  function finishPreview() {
    const a = audioRef.current;
    if (a) {
      try { a.pause(); } catch { /* noop */ }
    }
    setIsPlaying(false);
    setEnded(true);
    setProgress(1);
    void logMayaEvent({
      data: {
        event_type: "audio_preview_completed",
        anon_session_id: anonId,
        destination: destinationKey,
        lang,
      },
    });
  }

  async function handlePlay() {
    const a = audioRef.current;
    if (!a) return;
    if (limitReached) {
      setEnded(true);
      return;
    }
    if (isPlaying) {
      a.pause();
      return;
    }
    if (!deviceId) {
      setPlayError(labels.error);
      return;
    }
    setPlayError(null);
    setStarting(true);
    try {
      const play = await startMayaPreviewPlay({
        data: {
          destinationKey,
          destinationName,
          location: location ?? null,
          summary: summary ?? null,
          lang,
          deviceId,
        },
      });
      a.src = play.url;
      try { a.currentTime = 0; } catch { /* noop */ }
      setEnded(false);
      setProgress(0);
      await a.play();
      if (play.playsRemaining <= 0) setLimitReached(true);
      void logMayaEvent({
        data: {
          event_type: "audio_preview_started",
          anon_session_id: anonId,
          destination: destinationKey,
          lang,
        },
      });
    } catch (err) {
      const msg = err instanceof Error ? err.message : "";
      if (msg.includes("PREVIEW_LIMIT_REACHED")) {
        setLimitReached(true);
        setEnded(true);
      } else {
        setPlayError(labels.error);
      }
    } finally {
      setStarting(false);
    }
  }

  function handleEnded() {
    finishPreview();
  }

  function handleTime() {
    const a = audioRef.current;
    if (!a) return;
    if (a.currentTime >= PREVIEW_MAX_SECONDS) {
      finishPreview();
      return;
    }
    setProgress(Math.min(1, a.currentTime / PREVIEW_MAX_SECONDS));
  }

  return (
    <div className="mt-4 overflow-hidden rounded-2xl border border-primary/30 bg-gradient-to-br from-primary/10 via-card to-accent/10 p-4 sm:p-5">
      <div className="flex items-start gap-3">
        <div className="grid h-12 w-12 shrink-0 place-items-center rounded-xl bg-white ring-1 ring-primary/20">
          <img loading="lazy" decoding="async" src={mayaIcon} alt="Maya" width={48} height={48} className="h-11 w-11 rounded-lg object-contain" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-primary">
            <Sparkles className="h-3.5 w-3.5" /> {labels.badge}
          </div>
          <h3 className="mt-1 font-display text-base font-semibold leading-tight sm:text-lg">
            {labels.title}
          </h3>
          <p className="mt-0.5 text-xs text-muted-foreground">{labels.hint}</p>
        </div>
      </div>

      <audio
        ref={audioRef}
        preload="none"
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
        onEnded={handleEnded}
        onTimeUpdate={handleTime}
        className="hidden"
      />

      <div className="mt-4 flex items-center gap-3">
        <button
          type="button"
          onClick={handlePlay}
          disabled={isLoading || starting || limitReached}
          className={cn(
            "inline-flex h-11 items-center gap-2 rounded-full bg-primary px-4 text-sm font-semibold text-primary-foreground shadow transition hover:opacity-90 disabled:opacity-60",
          )}
        >
          {isLoading || starting ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : isPlaying ? (
            <Pause className="h-4 w-4" />
          ) : (
            <Play className="h-4 w-4" />
          )}
          {isLoading || starting ? labels.preparing : isPlaying ? labels.pause : labels.play}
        </button>
        <div className="relative h-2 flex-1 overflow-hidden rounded-full bg-muted">
          <div
            className="absolute inset-y-0 left-0 bg-primary transition-[width] duration-200"
            style={{ width: `${Math.round(progress * 100)}%` }}
          />
        </div>
        <div className="grid h-9 w-9 place-items-center rounded-full bg-foreground/10 text-foreground/70" aria-hidden="true">
          <Lock className="h-4 w-4" />
        </div>
      </div>

      {(error || playError) && !limitReached && (
        <p className="mt-3 rounded-xl border border-destructive/30 bg-destructive/10 px-3 py-2 text-xs text-destructive">
          {playError ?? labels.error}{" "}
          <button onClick={() => { setPlayError(null); void refetch(); }} className="underline">
            ↻
          </button>
        </p>
      )}

      {ended && (
        <div className="mt-4 rounded-2xl border border-primary/40 bg-card/80 p-4 text-center">
          <p className="font-display text-base font-semibold">{labels.ended}</p>
          <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:justify-center">
            <button
              type="button"
              onClick={() => onUnlockClick?.("destination")}
              className="inline-flex h-11 items-center justify-center gap-2 rounded-full bg-primary px-5 text-sm font-semibold text-primary-foreground shadow hover:opacity-90"
            >
              {labels.unlock}
            </button>
            <button
              type="button"
              onClick={() => onUnlockClick?.("pass")}
              className="inline-flex h-11 items-center justify-center gap-2 rounded-full border border-primary/40 bg-background px-5 text-sm font-semibold text-foreground hover:bg-primary/5"
            >
              {labels.pass}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
